class NinkearAdvancedMenu extends HTMLElement {
  connectedCallback() {
    this.mountConfiguredMenus();
    this.observeHeaderHydration();
    this.addEventListener('pointerover', this.onPointerOver);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.headerObserver?.disconnect();
    if (this.remountFrame) cancelAnimationFrame(this.remountFrame);
    this.removeEventListener('pointerover', this.onPointerOver);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('click', this.onClick);
  }

  normalize(value) {
    return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  mountConfiguredMenus() {
    const source = this.querySelector('[data-nk-menu-config-source]');
    if (!source) return;

    const menuItems = Array.from(this.querySelectorAll('.menu-list__list-item'));
    const configs = Array.from(source.querySelectorAll(':scope > [data-nk-menu-config]'));
    const preserveSource = !window.Shopify?.designMode;

    configs.forEach((config, index) => {
      const targetTitle = this.normalize(config.dataset.nkMenuTitle);
      if (!targetTitle) return;

      const item = menuItems.find((candidate) => {
        const link = candidate.querySelector(':scope > [ref="menuitem"]');
        const title = link?.querySelector('.menu-list__link-title');
        return this.normalize(title?.textContent) === targetTitle;
      });
      if (!item) return;

      const mountedConfig = item.querySelector(':scope > .menu-list__submenu [data-nk-menu-config]');
      if (mountedConfig && this.normalize(mountedConfig.dataset.nkMenuTitle) === targetTitle) return;

      const link = item.querySelector(':scope > [ref="menuitem"]');
      if (!link) return;

      let submenu = item.querySelector(':scope > .menu-list__submenu');
      let inner = submenu?.querySelector(':scope > .menu-list__submenu-inner');

      if (!submenu) {
        submenu = document.createElement('div');
        submenu.setAttribute('ref', 'submenu[]');
        item.appendChild(submenu);
      }
      if (!inner) {
        inner = document.createElement('div');
        inner.className = 'menu-list__submenu-inner';
        submenu.appendChild(inner);
      }

      const submenuId = `nk-configured-submenu-${index + 1}`;
      const renderedConfig = preserveSource ? config.cloneNode(true) : config;
      inner.id = submenuId;
      inner.replaceChildren(renderedConfig);
      submenu.className = `menu-list__submenu menu-list__submenu--ninkear menu-list__submenu--${renderedConfig.dataset.nkMenuKind || 'product'}`;

      link.setAttribute('aria-controls', submenuId);
      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-expanded', 'false');
      if (!link.querySelector('.nk-menu-caret')) {
        const caret = document.createElement('span');
        caret.className = 'nk-menu-caret';
        caret.setAttribute('aria-hidden', 'true');
        link.querySelector('.menu-list__link-title')?.appendChild(caret);
      }

      if (renderedConfig.dataset.nkMenuKind === 'product') this.initializeSeriesMenu(renderedConfig);
    });

    // The live storefront hydrates the header after the first paint. Keep an
    // untouched source there so the configured menu can be restored after the
    // theme replaces the header-menu children. In the editor we keep moving the
    // original nodes so Shopify's block-selection controls continue to work.
    if (!preserveSource) source.remove();
  }

  observeHeaderHydration() {
    if (window.Shopify?.designMode || this.headerObserver) return;

    const headerMenu = this.querySelector('header-menu');
    if (!headerMenu) return;

    this.headerObserver = new MutationObserver(() => {
      if (this.remountFrame) cancelAnimationFrame(this.remountFrame);
      this.remountFrame = requestAnimationFrame(() => {
        this.remountFrame = 0;
        this.mountConfiguredMenus();
      });
    });
    this.headerObserver.observe(headerMenu, { childList: true, subtree: true });
  }

  initializeSeriesMenu(menu) {
    const source = menu.querySelector('[data-nk-series-source]');
    const tabs = menu.querySelector('[data-nk-generated-tabs]');
    const panels = menu.querySelector('[data-nk-generated-panels]');
    if (!source || !tabs || !panels) return;

    const series = Array.from(source.querySelectorAll(':scope > [data-nk-series-config]'));
    series.forEach((seriesConfig, index) => {
      const id = seriesConfig.dataset.seriesId || `series-${index + 1}`;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `nk-series-menu__tab${index === 0 ? ' is-active' : ''}`;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.dataset.nkSeriesTab = id;

      const title = document.createElement('strong');
      title.textContent = seriesConfig.dataset.seriesTitle || '';
      button.appendChild(title);
      if (seriesConfig.dataset.seriesDescription) {
        const description = document.createElement('small');
        description.textContent = seriesConfig.dataset.seriesDescription;
        button.appendChild(description);
      }
      tabs.appendChild(button);

      const panel = seriesConfig.querySelector('[data-nk-series-panel]');
      if (panel) {
        panel.classList.toggle('is-active', index === 0);
        panel.hidden = index !== 0;
        panels.appendChild(panel);
      }
    });
    source.remove();
  }

  activate(tab) {
    const menu = tab.closest('[data-nk-mega-menu], [data-nk-menu-config]');
    if (!menu) return;
    const id = tab.dataset.nkSeriesTab;
    menu.querySelectorAll('[data-nk-series-tab]').forEach((item) => {
      const active = item === tab;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    menu.querySelectorAll('[data-nk-series-panel]').forEach((panel) => {
      const active = panel.dataset.nkSeriesPanel === id;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });
  }

  onPointerOver = (event) => {
    const tab = event.target.closest('[data-nk-series-tab]');
    if (tab) this.activate(tab);
  };

  onFocusIn = (event) => {
    const tab = event.target.closest('[data-nk-series-tab]');
    if (tab) this.activate(tab);
  };

  onClick = (event) => {
    const tab = event.target.closest('[data-nk-series-tab]');
    if (tab) this.activate(tab);
  };
}

if (!customElements.get('ninkear-advanced-menu')) {
  customElements.define('ninkear-advanced-menu', NinkearAdvancedMenu);
}